import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetSpendingSummaryQuery } from "api/orderApi";
import {
  MainContainer,
  InnerContainer,
  PageHeader,
  PageTitle,
  PageSub,
  HeaderRight,
  PeriodSelect,
  ExportBtn,
  KpiGrid,
  Kpi,
  KpiLabel,
  KpiValue,
  KpiChange,
  Row,
  Card,
  CardHeader,
  CardTitle,
  CardSub,
  TinyText,
  BarChart,
  BarGroup,
  BarValue,
  BarWrap,
  Bar,
  BarLabel,
  BreakdownList,
  BreakdownRow,
  BreakdownLeft,
  Dot,
  BreakdownName,
  MiniBarWrap,
  MiniBarFill,
  BreakdownPct,
  BreakdownAmount,
  AovBig,
  AovDelta,
  TxTable,
  TxTableScroll,
  Pill,
} from "./styles";

const getLastSixMonths = () =>
  Array.from({ length: 6 }, (_, index) => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - (5 - index));
    return {
      month: d.toLocaleDateString("en-US", { month: "short" }),
      amount: 0,
    };
  });

const MP_COLORS = {
  Shopify: "#96bf48",
  eBay: "#e53238",
  Amazon: "#ff9900",
  TikTok: "#ff0050",
  Walmart: "#0071ce",
  Unknown: "#9ca3af",
};

const STATUS_STYLES = {
  Charged: { bg: "#dcfce7", color: "#16a34a" },
  "Pending Payment": { bg: "#fef3c7", color: "#d97706" },
  Disputed: { bg: "#fee2e2", color: "#dc2626" },
};

const getStatusStyle = (status) =>
  STATUS_STYLES[status] || { bg: "#f3f4f6", color: "#374151" };

const formatMoney = (val) => `$${Number(val || 0).toFixed(2)}`;
const formatCompact = (val) => {
  const n = Number(val || 0);
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}k`;
  return `$${n.toFixed(0)}`;
};

const SpendingSummary = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("30");
  const { data } = useGetSpendingSummaryQuery(period);
  console.log(data);

  const metrics = useMemo(() => {
    const kpis = data?.kpis || {};
    const monthlySpend = data?.monthlySpend || getLastSixMonths();
    const maxMonth = Math.max(...monthlySpend.map((x) => Number(x.amount || 0)), 1);
    const breakdown = data?.marketplaceBreakdown || [];
    const transactions = (data?.recentTransactions || []).slice(0, 5).map((o) => ({
      orderId: o.orderId,
      product: o.product,
      marketplace: o.marketplace,
      date: new Date(o.date).toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      }),
      supplierCost: Number(o.supplierCost || 0),
      status: o.status,
    }));

    return {
      totalSpend: Number(kpis.totalSpend || 0),
      orderCount: Number(kpis.orderCount || 0),
      avgOrderValue: Number(kpis.averageOrderValue || 0),
      disputed: Number(kpis.openDisputes || 0),
      refunded: Number(kpis.totalRefunded || 0),
      monthlySpend,
      maxMonth,
      breakdown,
      transactions,
    };
  }, [data]);

  const csvExport = () => {
    const rows = [
      ["Order ID", "Product", "Marketplace", "Date", "Amount", "Status"],
      ...metrics.transactions.map((t) => [
        t.orderId,
        t.product,
        t.marketplace,
        t.date,
        t.supplierCost,
        t.status,
      ]),
    ];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "spending-summary.csv";
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <MainContainer>
      <InnerContainer>
        <PageHeader>
          <div>
            <PageTitle>Spending Summary</PageTitle>
            <PageSub>
              Full breakdown of your platform activity — transparent, detailed, real-time.
            </PageSub>
          </div>
          <HeaderRight>
            <PeriodSelect value={period} onChange={(e) => setPeriod(e.target.value)}>
              <option value="30">Last 30 days</option>
              <option value="90">Last 3 months</option>
              <option value="180">Last 6 months</option>
              <option value="all">All time</option>
            </PeriodSelect>
            <ExportBtn onClick={csvExport}>Export CSV</ExportBtn>
          </HeaderRight>
        </PageHeader>

        <KpiGrid>
          <Kpi>
            <KpiLabel>Total Spend</KpiLabel>
            <KpiValue>{formatMoney(metrics.totalSpend)}</KpiValue>
            <KpiChange>↑ Based on selected period</KpiChange>
          </Kpi>
          <Kpi>
            <KpiLabel>Orders Placed</KpiLabel>
            <KpiValue>{metrics.orderCount}</KpiValue>
            <KpiChange>↑ Completed + pending</KpiChange>
          </Kpi>
          <Kpi>
            <KpiLabel>Average Order Value</KpiLabel>
            <KpiValue>{formatMoney(metrics.avgOrderValue)}</KpiValue>
            <KpiChange>↑ Auto-calculated</KpiChange>
          </Kpi>
          <Kpi>
            <KpiLabel>Refunds & Disputes</KpiLabel>
            <KpiValue>{formatMoney(metrics.refunded)}</KpiValue>
            <KpiChange $down>↑ {metrics.disputed} open disputes</KpiChange>
          </Kpi>
        </KpiGrid>

        <Row $cols="1.6fr 1fr">
          <Card>
            <CardHeader>
              <div>
                <CardTitle>Monthly Spend Trend</CardTitle>
                <CardSub>Your platform spend over the last 6 months</CardSub>
              </div>
            </CardHeader>
            <BarChart>
              {metrics.monthlySpend.map((m, idx) => {
                const h = Math.max(8, (m.amount / metrics.maxMonth) * 100);
                const highlight = idx === metrics.monthlySpend.length - 1;
                return (
                  <BarGroup key={m.month}>
                    <BarValue>{formatCompact(m.amount)}</BarValue>
                    <BarWrap>
                      <Bar $height={h} $highlight={highlight} />
                    </BarWrap>
                    <BarLabel $highlight={highlight}>{m.month}</BarLabel>
                  </BarGroup>
                );
              })}
            </BarChart>
          </Card>

          <Card>
            <CardHeader>
              <div>
                <CardTitle>Spend by Marketplace</CardTitle>
                <CardSub>Where your orders are coming from</CardSub>
              </div>
            </CardHeader>
            <BreakdownList>
              {metrics.breakdown.map((row) => (
                <BreakdownRow key={row.name}>
                  <BreakdownLeft>
                    <Dot $color={MP_COLORS[row.name]} />
                    <BreakdownName>{row.name}</BreakdownName>
                    <MiniBarWrap>
                      <MiniBarFill $pct={row.pct} $color={MP_COLORS[row.name]} />
                    </MiniBarWrap>
                  </BreakdownLeft>
                  <BreakdownPct>{row.pct}%</BreakdownPct>
                  <BreakdownAmount>{formatCompact(row.amount)}</BreakdownAmount>
                </BreakdownRow>
              ))}
            </BreakdownList>
          </Card>
        </Row>

        <Row $cols="1fr 1fr">
          <Card>
            <CardHeader>
              <div>
                <CardTitle>Refunds & Disputes</CardTitle>
                <CardSub>Last selected period</CardSub>
              </div>
            </CardHeader>
            <KpiGrid style={{ gridTemplateColumns: "1fr 1fr", marginBottom: 12 }}>
              <Kpi style={{ background: "#fef2f2" }}>
                <KpiLabel>Total Refunded</KpiLabel>
                <KpiValue style={{ color: "#dc2626", fontSize: 22 }}>
                  {formatMoney(metrics.refunded)}
                </KpiValue>
              </Kpi>
              <Kpi>
                <KpiLabel>Open Disputes</KpiLabel>
                <KpiValue style={{ color: "#E8521A", fontSize: 22 }}>{metrics.disputed}</KpiValue>
              </Kpi>
            </KpiGrid>
          </Card>

          <Card>
            <CardHeader>
              <div>
                <CardTitle>Avg. Order Value</CardTitle>
                <CardSub>Per marketplace breakdown</CardSub>
              </div>
            </CardHeader>
            <AovBig>{formatMoney(metrics.avgOrderValue)}</AovBig>
            <AovDelta>↑ Computed from available orders</AovDelta>
            <BreakdownList>
              {metrics.breakdown.map((row) => (
                <BreakdownRow key={`${row.name}-aov`}>
                  <BreakdownLeft>
                    <Dot $color={MP_COLORS[row.name]} />
                    <BreakdownName>{row.name}</BreakdownName>
                    <MiniBarWrap>
                      <MiniBarFill $pct={row.pct} $color={MP_COLORS[row.name]} />
                    </MiniBarWrap>
                  </BreakdownLeft>
                  <BreakdownPct />
                  <BreakdownAmount>
                    {formatMoney(
                      row.pct ? (metrics.avgOrderValue * row.pct) / 100 : 0
                    )}
                  </BreakdownAmount>
                </BreakdownRow>
              ))}
            </BreakdownList>
          </Card>
        </Row>

        <Card>
          <CardHeader>
            <div>
              <CardTitle>Recent Transactions</CardTitle>
              <CardSub>Every charge from EcomDropship — fully transparent</CardSub>
            </div>
            <TinyText
              role="button"
              tabIndex={0}
              onClick={() => navigate("/my-orders")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate("/my-orders");
                }
              }}
            >
              View all →
            </TinyText>
          </CardHeader>
          <TxTableScroll>
            <TxTable>
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Product</th>
                  <th>Marketplace</th>
                  <th>Date</th>
                  <th>Supplier Cost</th>
                  <th>Status</th>
                  <th>Amount Charged</th>
                </tr>
              </thead>
              <tbody>
                {metrics.transactions.map((tx) => (
                  <tr key={tx.orderId}>
                    <td style={{ fontWeight: 500 }}>#{tx.orderId}</td>
                    <td>{tx.product}</td>
                    <td>
                      <Pill
                        $bg={`${MP_COLORS[tx.marketplace]}20`}
                        $color={MP_COLORS[tx.marketplace]}
                      >
                        {tx.marketplace}
                      </Pill>
                    </td>
                    <td>{tx.date}</td>
                    <td style={{ color: "#9ca3af" }}>{formatMoney(tx.supplierCost)}</td>
                    <td>
                      <Pill
                        $bg={getStatusStyle(tx.status).bg}
                        $color={getStatusStyle(tx.status).color}
                      >
                        {tx.status}
                      </Pill>
                    </td>
                    <td style={{ fontFamily: "Syne", fontWeight: 700 }}>
                      {formatMoney(tx.supplierCost)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </TxTable>
          </TxTableScroll>
        </Card>
      </InnerContainer>
    </MainContainer>
  );
};

export default SpendingSummary;
